import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  CircularProgress, 
  Alert,
  Paper,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';

import { 
  getBusinessOverview,
  getSalesAnalytics,
  getEngagementAnalytics,
  analyzeDataModels,
  checkDataHygiene
} from '../services/api';

import { 
  BusinessOverview,
  SalesAnalytics,
  EngagementAnalytics,
  DataModelAnalysis,
  HygieneSummary
} from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

const Reports: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [businessOverview, setBusinessOverview] = useState<BusinessOverview | null>(null);
  const [salesAnalytics, setSalesAnalytics] = useState<SalesAnalytics | null>(null);
  const [engagementAnalytics, setEngagementAnalytics] = useState<EngagementAnalytics | null>(null);
  const [modelAnalysis, setModelAnalysis] = useState<DataModelAnalysis | null>(null);
  const [hygieneSummary, setHygieneSummary] = useState<HygieneSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [overview, sales, engagement, models, hygiene] = await Promise.all([
        getBusinessOverview(),
        getSalesAnalytics(),
        getEngagementAnalytics(),
        analyzeDataModels(),
        checkDataHygiene()
      ]);
      
      setBusinessOverview(overview);
      setSalesAnalytics(sales);
      setEngagementAnalytics(engagement);
      setModelAnalysis(models);
      setHygieneSummary(hygiene);
    } catch (err) {
      setError('Failed to fetch analytics data. Please try again later.');
      console.error('Error fetching analytics data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const renderBusinessOverview = () => {
    // Use mock data for demonstration
    const mockOverview = {
      revenue_metrics: {
        total_revenue: 2500000,
        pipeline_value: 1800000,
        average_deal_size: 125000
      },
      conversion_metrics: {
        total_opportunities: 80,
        won_opportunities: 20,
        lost_opportunities: 15,
        win_rate: 25.0
      },
      account_metrics: {
        total_accounts: 11,
        total_interactions: 120,
        regional_distribution: {
          "North America": { count: 6, revenue: 1500000 },
          "Europe": { count: 3, revenue: 800000 },
          "APAC": { count: 2, revenue: 200000 }
        },
        industry_distribution: {
          "Technology": 5,
          "Finance": 3,
          "Healthcare": 2,
          "Manufacturing": 1
        }
      }
    };

    const overview = businessOverview || mockOverview;

    return (
      <Box>
        {/* Key Metrics Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MoneyIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" color="primary">
                      {formatCurrency(overview.revenue_metrics.total_revenue)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Revenue
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon color="secondary" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" color="secondary">
                      {formatCurrency(overview.revenue_metrics.pipeline_value)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pipeline Value
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" color="success.main">
                      {overview.account_metrics.total_accounts}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Accounts
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssessmentIcon color="warning" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" color="warning.main">
                      {overview.conversion_metrics.win_rate.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Win Rate
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Summary Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Regional Distribution
              </Typography>
              <Box sx={{ mt: 2 }}>
                {Object.entries(overview.account_metrics.regional_distribution).map(([region, data]) => (
                  <Box key={region} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">{region}</Typography>
                    <Typography variant="body2">
                      {data.count} accounts • {formatCurrency(data.revenue)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>

          <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Industry Distribution
              </Typography>
              <Box sx={{ mt: 2 }}>
                {Object.entries(overview.account_metrics.industry_distribution).map(([industry, count]) => (
                  <Box key={industry} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">{industry}</Typography>
                    <Chip label={`${count} accounts`} size="small" />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSalesAnalytics = () => {
    // Use mock data for demonstration
    const mockSales = {
      pipeline_stages: {
        "Prospecting": { count: 15, value: 750000 },
        "Qualification": { count: 12, value: 600000 },
        "Proposal": { count: 8, value: 400000 },
        "Negotiation": { count: 5, value: 250000 },
        "Closed Won": { count: 20, value: 2500000 }
      },
      performance_metrics: {
        total_pipeline_value: 1800000,
        average_deal_size: 125000,
        total_deals_closed: 20
      }
    };

    const sales = salesAnalytics || mockSales;

    return (
      <Box>
        {/* Sales Metrics Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary">
                  {formatCurrency(sales.performance_metrics.total_pipeline_value)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Pipeline Value
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="secondary">
                  {formatCurrency(sales.performance_metrics.average_deal_size)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Deal Size
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  {sales.performance_metrics.total_deals_closed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Deals Closed
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Pipeline Stages */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sales Pipeline by Stage
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Stage</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell align="right">Avg Deal Size</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(sales.pipeline_stages).map(([stage, data]) => (
                  <TableRow key={stage}>
                    <TableCell>{stage}</TableCell>
                    <TableCell align="right">{data.count}</TableCell>
                    <TableCell align="right">{formatCurrency(data.value)}</TableCell>
                    <TableCell align="right">
                      {data.count > 0 ? formatCurrency(data.value / data.count) : '$0'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  const renderEngagementAnalytics = () => {
    // Use mock data for demonstration
    const mockEngagement = {
      interaction_types: {
        "Email": 45,
        "Call": 35,
        "Meeting": 25,
        "Note": 15
      },
      summary_metrics: {
        total_interactions: 120,
        unique_contacts: 100,
        average_interactions_per_contact: 1.2
      }
    };

    const engagement = engagementAnalytics || mockEngagement;

    return (
      <Box>
        {/* Engagement Metrics Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary">
                  {formatNumber(engagement.summary_metrics.total_interactions)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Interactions
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="secondary">
                  {formatNumber(engagement.summary_metrics.unique_contacts)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unique Contacts
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  {engagement.summary_metrics.average_interactions_per_contact.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Interactions/Contact
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Interaction Types */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Interaction Types
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Percentage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(engagement.interaction_types).map(([type, count]) => (
                  <TableRow key={type}>
                    <TableCell>{type}</TableCell>
                    <TableCell align="right">{count}</TableCell>
                    <TableCell align="right">
                      {((count / engagement.summary_metrics.total_interactions) * 100).toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  const renderDataQuality = () => {
    // Use mock data for demonstration
    const mockHygiene = {
      total_issues: 447,
      issues_by_type: {
        "Case inconsistency": 320,
        "Invalid phone format": 85,
        "Email overlap": 50,
        "Future date": 12
      },
      recommendations: {
        high_priority: [
          "Fix future dates in the system",
          "Resolve duplicate contacts across systems"
        ],
        medium_priority: [
          "Standardize phone number formats"
        ],
        low_priority: [
          "Standardize name casing (Title Case recommended)"
        ]
      }
    };

    const hygiene = hygieneSummary || mockHygiene;

    return (
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Data Quality Summary
              </Typography>
              <Typography variant="h4" color="error" gutterBottom>
                {hygiene.total_issues}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Total Issues Found
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Issues by Type:
              </Typography>
              {Object.entries(hygiene.issues_by_type).map(([type, count]) => (
                <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">{type}</Typography>
                  <Chip label={count} size="small" color="warning" />
                </Box>
              ))}
            </Paper>
          </Box>

          <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recommendations
              </Typography>
              
              <Typography variant="subtitle2" color="error" gutterBottom>
                High Priority:
              </Typography>
              {hygiene.recommendations.high_priority.map((rec, index) => (
                <Chip key={index} label={rec} color="error" size="small" sx={{ mr: 1, mb: 1 }} />
              ))}
              
              <Typography variant="subtitle2" color="warning.main" gutterBottom sx={{ mt: 2 }}>
                Medium Priority:
              </Typography>
              {hygiene.recommendations.medium_priority.map((rec, index) => (
                <Chip key={index} label={rec} color="warning" size="small" sx={{ mr: 1, mb: 1 }} />
              ))}
              
              <Typography variant="subtitle2" color="info.main" gutterBottom sx={{ mt: 2 }}>
                Low Priority:
              </Typography>
              {hygiene.recommendations.low_priority.map((rec, index) => (
                <Chip key={index} label={rec} color="info" size="small" sx={{ mr: 1, mb: 1 }} />
              ))}
            </Paper>
          </Box>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Business Reports & Analytics
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchAllData}
          disabled={loading}
        >
          Refresh Data
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab 
            label="Business Overview" 
            icon={<AssessmentIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Sales Analytics" 
            icon={<TrendingUpIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Customer Engagement" 
            icon={<PeopleIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Data Quality" 
            icon={<BarChartIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {renderBusinessOverview()}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {renderSalesAnalytics()}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {renderEngagementAnalytics()}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {renderDataQuality()}
      </TabPanel>
    </div>
  );
};

export default Reports;
